import { integrationService } from "../services/integration-service.js";
import { reportsModelingService } from "../services/reports-modeling-service";
import { RoleMenuNode } from "../types/common/modeling/modeling-types";
import { TableFilterData } from "../types/common/table/table-types";
import { setClassCheckedIndeterminate } from "./reports-modeling-utils";
import { TreeWalker, createTreeNode, findItemInTree, modifyItemAndRecalcParentsFields, modifyItemInTreeRecursive } from "./tree-table.js";

export function openRoleMenuNode(itemPath: string[], menuTree: RoleMenuNode[]) {
    const item = findItemInTree(itemPath, menuTree);

    if (item) {
        item.open = !item.open;
    }
}

export function addRoleMenuFolder(activeNodePath: string[], menuTree: RoleMenuNode[], folderName: string) {
    const item = findItemInTree(activeNodePath, menuTree);

    const newFolderNode = {
        reportName: folderName,
        reportType: "",
        isFolder: true,
        nodeText: folderName,
        description: "",
        children: [],
    };

    if (item?.children) {
        item.children.push(createTreeNode(newFolderNode, item.level + 1, item.path));
    } else {
        const activeParentNodePath = [...activeNodePath];
        activeParentNodePath.pop();

        const parentItem = findItemInTree(activeParentNodePath, menuTree);

        if (parentItem) {
            parentItem.children.push(createTreeNode(newFolderNode, item.level, parentItem.path));
        }
    }
}

export function addRoleMenuNode(activeNodePath, menuTree, tcodes, transactions) {
    const item = findItemInTree(activeNodePath, menuTree);

    const newNodes = transactions.map((transaction) => ({
        reportName: transaction.key,
        reportType: "TR",
        isFolder: false,
        nodeText: transaction.text,
        description: "",
    }));

    if (item?.children) {
        item.children = item.children.concat(newNodes.map((tcodeNode) => createTreeNode(tcodeNode, item.level + 1, item.path)));
    } else {
        const activeParentNodePath = [...activeNodePath];
        activeParentNodePath.pop();

        const parentItem = findItemInTree(activeParentNodePath, menuTree);

        if (parentItem && parentItem.children) {
            parentItem.children = parentItem.children.concat(newNodes.map((tcodeNode) => createTreeNode(tcodeNode, item.level, parentItem.path)));
        }
    }
}

export function removeRoleMenuNode(activeNodePath: string[], menuTree: RoleMenuNode[]) {
    const parentPath = activeNodePath.slice(0, -1);
    const itemId = activeNodePath[activeNodePath.length - 1];
    const parent = findItemInTree(parentPath, menuTree);
    parent.children = parent.children.filter((child) => child.rowId !== itemId);
}

function copyRoleMenuItem(roleMenuItem, rootId) {
    const menuNodeToCopy = {
        ...roleMenuItem,
        path: [...roleMenuItem.path],
    };

    delete menuNodeToCopy.checked;
    delete menuNodeToCopy.indeterminate;
    menuNodeToCopy.path[0] = rootId;

    if (menuNodeToCopy.children?.length) {
        menuNodeToCopy.children = menuNodeToCopy.children.map((child) => copyRoleMenuItem(child, rootId));
    }

    return menuNodeToCopy;
}

export function copyRoleMenu(menuToCopy, currentMenu) {
    if (!menuToCopy?.length) {
        return;
    }

    const rootId = currentMenu[0].rowId;

    for (const menuItem of menuToCopy) {
        if (!menuItem.children?.length) {
            continue;
        }

        const copiedMenuChildren = menuItem.children?.map((child) => copyRoleMenuItem(child, rootId));

        currentMenu[0].children.push(...copiedMenuChildren);
    }
}

export function getTcodesMapCountFromMenu(menu) {
    const tcodesMap = {};

    const menuWalker = new TreeWalker(menu);

    menuWalker.walk(
        (node) => {
            if (!node.isFolder) {
                if (!(node.reportName in tcodesMap)) {
                    tcodesMap[node.reportName] = 0;
                }

                tcodesMap[node.reportName] += 1;
            }
        },
        () => {
            return {
                keyField: "",
                keyValue: "",
                childField: "children",
            };
        },
        () => {}
    );

    return tcodesMap;
}

export function getDeletedTcodesFromMenu(tcodesMapItem: Record<string, number>, tcodesMapMenu: Record<string, number>) {
    const deletedTcodes = new Set<string>();
    const remainedTcodes = new Set<string>();
    const deletedAtBranchTcodes: string[] = [];

    Object.entries(tcodesMapItem).forEach(([tcode, count]) => {
        for (let i = 0; i < count; i++) {
            deletedAtBranchTcodes.push(tcode);
        }

        if (!(tcode in tcodesMapMenu)) {
            return;
        }

        const tcodeInMenuCount = tcodesMapMenu[tcode];

        if (tcodeInMenuCount - count === 0) {
            deletedTcodes.add(tcode);
        }
    });

    Object.keys(tcodesMapMenu).forEach((tcode) => {
        if (deletedTcodes.has(tcode)) {
            return;
        }

        remainedTcodes.add(tcode);
    });

    return { deletedTcodes, remainedTcodes, deletedAtBranchTcodes };
}

export function getTransactionFromRoleMenu(menuChildren: RoleMenuNode[]) {
    const transactions: string[] = [];

    for (const child of menuChildren) {
        if (child.reportType === "TR" && child.reportName) {
            transactions.push(child.reportName);
        }

        if (!child.children?.length) {
            continue;
        }

        const childrenTransactions = getTransactionFromRoleMenu(child.children);
        transactions.push(...childrenTransactions);
    }

    return transactions;
}

export function modifyCopyMenuTree(tree, itemPath, field, value) {
    if (field === "checked") {
        modifyItemAndRecalcParentsFields(
            tree,
            itemPath,
            (item) => {
                item.indeterminate = false;
                modifyItemInTreeRecursive(item, field, value);
            },
            setClassCheckedIndeterminate
        );
        return;
    }

    const item = findItemInTree(itemPath, tree);

    if (item) {
        item[field] = value;
    }
}

export async function getDefaultPermissionsValuesForTransactions(systemId, transactionIds, groupId) {
    const filters = [{ field: "systemId", sign: "EQ", value: systemId }];

    if (groupId) {
        filters.push({ field: "groupId", sign: "EQ", value: groupId });
    }

    if (transactionIds.length === 1) {
        filters.push({ field: "transaction", sign: "EQ", value: transactionIds[0] });
    } else {
        filters.push({ field: "transaction", sign: "IN", value: transactionIds });
    }

    const { rows } = await integrationService.getFunctionDefaultPermissions({
        filters,
    });

    return rows;
}

export async function getDefaultPermissionsValuesForTransactionsMap(systemId: string | null, transactionIds: string[], groupId: string | null =null) {
    const params: { systemId?: string; groupId?: string; filters?: TableFilterData[] } = {};
    const filters: TableFilterData[] = [];

    if (systemId) {
        params.systemId = systemId;
    }

    //TODO: уточнить насчет groupId
    if (groupId) {
        params.groupId = groupId;
    }

    if (transactionIds.length === 1) {
        filters.push({ field: "transaction", sign: "EQ", value: transactionIds[0] });
    } else {
        filters.push({ field: "transaction", sign: "IN", value: transactionIds.toString() });
    }

    params.filters = filters;

    const { rows } = await integrationService.getPermissionDefaultValuesForSystem(params);

    const result = {};

    transactionIds.forEach((tcode) => {
        result[tcode] = rows.filter((row) => row.permissionGroup === tcode);
    });

    return result;
}

export async function getTransactionsByIds(systemId, transactionIds) {
    const filters = [{ field: "systemId", sign: "EQ", value: systemId }];

    filters.push({ field: "transaction", sign: "IN", value: transactionIds });

    const { rows } = await integrationService.getTransactions({
        filters,
    });

    return rows;
}

export async function getTransactionsByIdsForSystemsGroup(groupId, transactionIds) {
    const filters = [{ field: "groupId", sign: "EQ", value: groupId }];

    filters.push({ field: "transaction", sign: "IN", value: transactionIds });

    const { rows } = await integrationService.getTransactions({
        filters,
    });

    return rows;
}

// export async function getNewPermissionsTree(systemId, permissionIds) {
//     // const newPermissionIds = filterNewPermissionsInRoleTree(
//     //     roleTree,
//     //     permissionIds
//     // );

//     if (!permissionIds.size) {
//         return [];
//     }

//     const permissionsTree = await reportsModelingService.getPermissionsTree(null, null, systemId, Array.from(permissionIds));

//     return permissionsTree.rows;
// }

export async function getPfcgTreeForTcodes(systemId, tcodesList) {
    const params = {
        transactions: tcodesList.join(","),
        system: systemId,
    };
    const rows = await integrationService.getPfcgTreeForTcode(params);

    return reportsModelingService.parsePermissionsTree(rows, new Set());
}

export function mergeDefaultValuesWithTcodePermissions(defaultValuesMap, tcodePermissions) {
    const tcodePermissionsMap = tcodePermissions.reduce((accumulator, tcodePermission) => {
        if (!(tcodePermission.transaction in accumulator)) {
            accumulator[tcodePermission.transaction] = new Set();
        }

        accumulator[tcodePermission.transaction].add(tcodePermission.permission);

        return accumulator;
    }, {});

    const defaultValuesMapTmp = {};

    Object.keys(defaultValuesMap).forEach((key) => {
        defaultValuesMapTmp[key] = new Set(defaultValuesMap[key].map((item) => item.permission));
    });

    Object.keys(tcodePermissionsMap).forEach((key) => {
        const permissionSet = tcodePermissionsMap[key];

        permissionSet.forEach((permission) => {
            if (!defaultValuesMapTmp[key].has(permission)) {
                defaultValuesMap[key].push({
                    permission: permission,
                    field: "",
                    permissionGroup: key,
                    system: "",
                    valueFrom: "",
                    valueTo: "",
                });
            }
        });
    });

    return defaultValuesMap;
}
